import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError, type Tournament, type TournamentSettings } from "../lib/api";
import { PUBLISH_FOCUS_PARAM } from "../lib/draftWizard";
import { canAdvanceFromSetupStep, setupGate, type SetupGateRow, type SetupReadinessState } from "../lib/setupGates";
import { useTournamentMutationTick } from "../lib/useTournamentMutationTick";
import FieldHint from "./FieldHint";

/** Cierre del wizard: «Publicar el torneo».
 * - Eduardo 2026-09-23: vivía en Comunicación; pasó al final del Draw (paso 9).
 * - Eduardo 2026-10-05: pasa al final de Calendario (paso 10), después de
 *   revisar las fechas; en el Draw solo queda «Continuar a Calendario».
 * Publicar un borrador es irreversible y falla cerrado si falta Formato,
 * Grupos o Draw en alguna categoría (las mismas condiciones de antes; el
 * calendario de canchas nunca bloquea). Solo se ve con el torneo en
 * borrador; al publicar, el admin se queda en Calendario (`onPublished`). */
export default function TournamentPublishCard({
  tournamentId,
  onPublished,
}: {
  tournamentId: number;
  onPublished?: (tournament: Tournament) => void;
}) {
  const { token } = useAuth();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [settings, setSettings] = useState<TournamentSettings | null>(null);
  const [readinessRows, setReadinessRows] = useState<SetupGateRow[]>([]);
  const [readinessState, setReadinessState] = useState<SetupReadinessState>("loading");
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fees de Torneos123 pendientes: el mensaje lleva «Pagar ahora».
  const [feesBlocked, setFeesBlocked] = useState(false);
  const [searchParams] = useSearchParams();
  const cardRef = useRef<HTMLElement | null>(null);
  const publishButtonRef = useRef<HTMLButtonElement | null>(null);
  const focusedRef = useRef(false);
  const mutationTick = useTournamentMutationTick(tournamentId);

  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    void api.tournaments
      .get(token, tournamentId)
      .then((response) => {
        if (cancelled) return;
        setTournament(response.data.tournament);
        setSettings(response.data.settings);
      })
      .catch(() => {
        if (!cancelled) setError("No pudimos cargar la configuración del torneo.");
      });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId]);

  // Lo mismo que bloquea el paso 10 del stepper (Formato, Grupos y Draw en
  // todas las categorías): antes lo pasaba el Draw, ahora lo pide la tarjeta.
  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    if (mutationTick === 0) setReadinessState("loading");
    void api.matches
      .scheduleReadiness(token, tournamentId)
      .then((response) => {
        if (cancelled) return;
        setReadinessRows(response.data.divisions);
        setReadinessState("loaded");
      })
      .catch(() => {
        if (!cancelled && mutationTick === 0) setReadinessState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, mutationTick]);

  const canPublish = canAdvanceFromSetupStep(readinessRows, 9, readinessState);
  const blockedReason =
    readinessState === "loading"
      ? "Verificando el estado de todas las categorías…"
      : readinessState === "failed"
        ? "No pudimos verificar que Formato, Grupos y Draw estén completos. Recarga la página para intentar otra vez."
        : setupGate(readinessRows).reason ?? (canPublish ? null : "Completa el draw de todas las categorías.");
  const isDraft = tournament?.status === "draft";
  const focusRequested = searchParams.get(PUBLISH_FOCUS_PARAM) === "1";

  // Borrador regresado al asistente (Eduardo 2026-10-02): con todo listo, la
  // tarjeta se enfoca sola para que publicar sea lo siguiente. Si aún falta
  // algo no se baja: arriba, los pasos del asistente dicen qué falta.
  useEffect(() => {
    if (!focusRequested || focusedRef.current || !isDraft || !canPublish || !settings) return;
    focusedRef.current = true;
    cardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    publishButtonRef.current?.focus({ preventScroll: true });
  }, [focusRequested, isDraft, canPublish, settings]);

  async function handlePublish() {
    if (!token || !tournament || !settings || !isDraft) return;
    setFinishing(true);
    setError(null);
    setFeesBlocked(false);
    try {
      // Re-verificación fail-closed (URL directa, historial, bookmark).
      const readiness = await api.matches.scheduleReadiness(token, tournament.id);
      const gate = setupGate(readiness.data.divisions);
      if (gate.maxStep < 10) {
        setReadinessRows(readiness.data.divisions);
        setError(gate.reason ?? "Completa Formato, Grupos y Draw en todas las categorías antes de publicar.");
        return;
      }
      await api.tournaments.updateSettings(token, tournament.id, {
        allow_post_publish_changes: settings.allow_post_publish_changes,
      });
      const response = await api.tournaments.update(token, tournament.id, { status: "active" });
      const published = response?.data?.tournament ?? { ...tournament, status: "active" };
      // El admin se queda en Calendario: la página pasa a «Empezar torneo».
      setTournament(published);
      onPublished?.(published);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
      if (err instanceof ApiError && (err.code === "fees_unpaid_publish_blocked" || err.code === "fees_account_blocked")) {
        setFeesBlocked(true);
      }
    } finally {
      setFinishing(false);
    }
  }

  // Publicado (o aún cargando): no hay nada que publicar aquí. Si no se pudo
  // leer el torneo, la tarjeta sí sale para decirlo.
  if (tournament ? !isDraft : !error) return null;

  return (
    <section className="card aviso-cierre" id="publicar-torneo" ref={cardRef} data-no-toast>
      <div>
        <p className="card__eyebrow">Último paso</p>
        <h2 className="card__title">Publicar el torneo</h2>
        <p className="card__subtitle">Al publicar, esto es lo que pasa:</p>
        <ul className="aviso-checklist">
          <li>El torneo pasa a activo y aparece para los jugadores.</li>
          <li data-publish-schedules-note>
            Los horarios que ya programaste se publican junto con el torneo: no tienes que publicarlos aparte.
          </li>
          <li>Los mensajes salen por los canales que elegiste en Comunicación (paso 4).</li>
        </ul>
      </div>
      {settings ? (
        <label className="aviso-fila">
          <span className="aviso-fila__texto">
            <strong className="hint-label">
              Permitir cambios después de publicar
              <FieldHint>Si lo apagas, el formato y las categorías quedan congelados al publicar; solo resultados y calendario siguen editables.</FieldHint>
            </strong>
          </span>
          <input
            type="checkbox"
            checked={Boolean(settings.allow_post_publish_changes)}
            onChange={(e) => setSettings({ ...settings, allow_post_publish_changes: e.target.checked })}
            disabled={finishing}
          />
        </label>
      ) : null}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {feesBlocked ? (
        <p className="aviso-fila" data-fees-blocked>
          <Link className="button button--primary" to="/admin/finance#fees">
            Pagar ahora
          </Link>
        </p>
      ) : null}
      <div className="form__actions">
        {canPublish ? (
          <button
            ref={publishButtonRef}
            className="btn"
            type="button"
            onClick={() => void handlePublish()}
            disabled={finishing || !settings}
          >
            {finishing ? "Publicando…" : "Publicar torneo"}
          </button>
        ) : (
          <button
            className="btn"
            type="button"
            disabled
            title={blockedReason ?? undefined}
          >
            {readinessState === "loading" ? "Verificando categorías…" : "Completa todos los draws para publicar"}
          </button>
        )}
      </div>
      {!canPublish && readinessState !== "loading" && blockedReason ? (
        <p className="muted" data-publish-blocked-reason>{blockedReason}</p>
      ) : null}
    </section>
  );
}
